// Version: 1.7
// v1.7: edge_maintenance events (Update Images and Restart Services results, app.py v4.34) get their own label.
// v1.6: linux_desktop events (edge Linux desktop start, stop and auto-stop) get their own label.
// v1.5: Number tiles use the shared .stat-card; inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.4: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.3: Client chip shows the client's name (app.py v4.08), not its folder name.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

// ── Config ────────────────────────────────────────────────────────────────────

const EVENT_CONFIG = {
  edge_push:    { label: 'Edge Push',       color: '#00d4ff', icon: '⬡' },
  cyber_scan:   { label: 'Cyber Scan',      color: '#30d158', icon: '⊙' },
  force_run:    { label: 'Force Run',       color: '#e6b800', icon: '↺' },
  linux_desktop:{ label: 'Linux Desktop',   color: '#00d4ff', icon: '▣' },
  edge_maintenance:{ label: 'Edge Maintenance', color: '#e6b800', icon: '↑' },
  action1_check:{ label: 'Device Check',    color: '#bf5af2', icon: '◫' },
  m365_check:   { label: 'M365 Check',      color: '#bf5af2', icon: '⊞' },
  dns_check:    { label: 'DNS Check',       color: '#00d4ff', icon: '⊛' },
  saas_check:   { label: 'SaaS Check',      color: '#00d4ff', icon: '⊛' },
  report:       { label: 'Report',          color: '#30d158', icon: '⎘' },
  login:        { label: 'Login',           color: '#86868b', icon: '→' },
  error:        { label: 'Error',           color: '#ff2d55', icon: '✗' },
};

function fmtDT(iso) {
  if (!iso) return '—';
  try {
    const cleaned = iso.replace(/\+00:00Z$/, '+00:00').replace(/Z$/, '');
    const d = new Date(cleaned);
    if (isNaN(d)) return iso;
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'Europe/London',
    }) + ' GMT';
  } catch { return iso; }
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const cleaned = iso.replace(/\+00:00Z$/, '+00:00').replace(/Z$/, '');
    const secs = Math.floor((Date.now() - new Date(cleaned)) / 1000);
    if (secs < 60)    return `${secs}s ago`;
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return ''; }
}

// ── Activity entry row ────────────────────────────────────────────────────────

function ActivityEntry({ entry }) {
  const cfg   = EVENT_CONFIG[entry.event_type] || { label: entry.event_type, color: '#86868b', icon: '○' };
  const isErr = entry.status === 'error';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 0', borderBottom: '1px solid var(--border)',
    }}>
      {/* Icon */}
      <div style={{
        width: 28, height: 28, borderRadius: 6, flexShrink: 0,
        background: `${cfg.color}18`, border: `1px solid ${cfg.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, color: cfg.color, fontWeight: 700, marginTop: 1,
      }}>
        {cfg.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
          {entry.client_slug && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
              background: 'var(--surface)', border: '1px solid var(--border)',
              color: 'var(--text-muted)',
            }}>{entry.client_name || entry.client_slug.replace(/_/g, ' ')}</span>
          )}
          {entry.source && entry.source !== 'core' && (
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: 3, padding: '1px 6px',
            }}>{entry.source}</span>
          )}
          {isErr && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
              background: '#ff2d5518', border: '1px solid #ff2d5540', color: '#ff2d55',
            }}>Error</span>
          )}
        </div>

        {/* Detail — always fully visible, errors in mono box */}
        {isErr ? (
          <div style={{
            fontSize: 11, color: '#ff2d55', marginTop: 6,
            background: '#ff2d5508', border: '1px solid #ff2d5530',
            borderRadius: 4, padding: '6px 8px',
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            overflowWrap: 'anywhere',
          }}>
            {entry.detail}
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: 'var(--text)', marginTop: 2,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}>
            {entry.detail}
          </div>
        )}

        {entry.scan_type && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, fontFamily: 'var(--font-mono)' }}>
            {entry.scan_type}
          </div>
        )}
      </div>

      {/* Timestamp */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(entry.ts)}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{fmtDT(entry.ts)}</div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ActivityLog() {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [clients, setClients]   = useState([]);

  // Filters
  const [days, setDays]         = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ days, limit: 500 });
      if (clientFilter) params.set('client', clientFilter);
      if (sourceFilter) params.set('source', sourceFilter);
      if (statusFilter) params.set('status', statusFilter);
      const r = await authFetch(`/api/activity?${params}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load');
      setEntries(d.entries || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [days, clientFilter, sourceFilter, statusFilter]);

  useEffect(() => {
    load();
    authFetch('/api/clients').then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
  }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = search
    ? entries.filter(e =>
        e.detail?.toLowerCase().includes(search.toLowerCase()) ||
        e.client_slug?.toLowerCase().includes(search.toLowerCase()) ||
        e.event_type?.toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  // Stats
  const errCount  = entries.filter(e => e.status === 'error').length;
  const edgeCount = entries.filter(e => e.event_type === 'edge_push').length;
  const scanCount = entries.filter(e => e.event_type === 'cyber_scan').length;

  const selStyle = {};

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">Activity Log</div>
          <div className="page-subtitle">Scheduler runs, scan results and system events</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>↺ Refresh</button>
      </div>

      {/* Stats */}
      <div className="stat-tiles">
        {[
          { label: 'Total events',  val: entries.length,  tone: 'accent' },
          { label: 'Edge pushes',   val: edgeCount,       tone: 'accent' },
          { label: 'Cyber scans',   val: scanCount,       tone: 'green' },
          { label: 'Errors',        val: errCount,        tone: errCount > 0 ? 'red' : 'muted' },
        ].map(({ label, val, tone }) => (
          <div key={label} className="stat-card">
            <div className={`stat-value ${tone}`}>{val}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input className="input-sm" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
          style={{ ...selStyle, minWidth: 180, flex: '1 1 180px' }} />

        <select className="input-sm" value={days} onChange={e => setDays(Number(e.target.value))} style={selStyle}>
          <option value={1}>Today</option>
          <option value={2}>Last 2 days</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
        </select>

        <select className="input-sm" value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={selStyle}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
        </select>

        <select className="input-sm" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={selStyle}>
          <option value="">All sources</option>
          <option value="edge">Edge</option>
          <option value="cyber">Cyber</option>
          <option value="scheduler">Scheduler</option>
          <option value="admin">Admin</option>
          <option value="core">Core</option>
        </select>

        <select className="input-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selStyle}>
          <option value="">All statuses</option>
          <option value="ok">OK</option>
          <option value="error">Errors only</option>
          <option value="warning">Warnings</option>
        </select>

        {(clientFilter || sourceFilter || statusFilter || search) && (
          <button className="btn btn-secondary btn-sm"
            onClick={() => { setClientFilter(''); setSourceFilter(''); setStatusFilter(''); setSearch(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          {entries.length === 0 ? 'No activity logged yet — events will appear here as scans run.' : 'No events match your filters.'}
        </div>
      ) : (
        <div className="card" style={{ padding: '0 20px' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '12px 0 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
            {search || clientFilter || sourceFilter || statusFilter ? ` (filtered from ${entries.length})` : ''}
          </div>
          {filtered.map((entry, i) => <ActivityEntry key={i} entry={entry} />)}
        </div>
      )}
    </div>
  );
}